import { useMemo } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { CustomerService } from 'Service/api/customer-service';
import { createFormDataWithCompression, formatFileSize } from '../utils/fileCompression';

export const useTransformOptions = (
  options,
  valueKey,
  labelKey,
  placeholderValue,
  placeholderLabel,
) => {
  return useMemo(() => {
    const safeOptions = options || [];
    return [
      { value: placeholderValue, label: placeholderLabel },
      ...safeOptions.map(option => ({
        value: option[valueKey],
        label: option[labelKey],
      })),
    ];
  }, [options, valueKey, labelKey, placeholderValue, placeholderLabel]);
};

export const useDocumentUpload = () => {
  const [loading, setLoading] = useState(false);
  const [compressionLoading, setCompressionLoading] = useState(false);
  const dispatch = useDispatch();

  const uploadDocuments = async (formData, options = {}) => {
    try {
      setLoading(true);
      
      // Extract file from FormData for compression check
      const file = formData.get('File') || formData.get('file');
      let processedFormData = formData;
      let compressionInfo = null;
      
      // If file exists and compression is enabled (default: true)
      if (file && options.enableCompression !== false) {
        try {
          console.log(`Processing file: ${file.name} (${formatFileSize(file.size)})`);
          
          // Show compression loading state
          setCompressionLoading(true);
          
          // Extract additional data from original FormData
          const additionalData = {};
          for (let [key, value] of formData.entries()) {
            if (key !== 'File' && key !== 'file') {
              additionalData[key] = value;
            }
          }
          
          // Create compressed FormData
          const result = await createFormDataWithCompression(file, additionalData);
          processedFormData = result.formData;
          compressionInfo = result.compressionInfo;
          
          if (compressionInfo.wasCompressed) {
            console.log(`File compressed: ${formatFileSize(compressionInfo.originalSize)} → ${formatFileSize(compressionInfo.compressedSize)} (${compressionInfo.compressionRatio}% reduction)`);
          }
          
          // Hide compression loading state
          setCompressionLoading(false);
        } catch (compressionError) {
          console.error('Compression failed:', compressionError);
          
          // Hide compression loading state
          setCompressionLoading(false);
          
          // Check if it's a size limit error
          if (compressionError.message.includes('Unable to compress file below 10MB limit')) {
            dispatch(
              showToast({
                message: `File too large: ${compressionError.message}. Please try with a smaller file.`,
                type: 'danger',
              }),
            );
            return null;
          } else {
            // For other compression errors, try with original file
            dispatch(
              showToast({
                message: `Compression failed: ${compressionError.message}. Trying original file...`,
                type: 'warning',
              }),
            );
          }
        }
      }
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      
      const response = await CustomerService.uploadDocument(processedFormData, config);

      if (response.status === 200) {
        let successMessage = 'Document uploaded successfully';
        
        if (compressionInfo) {
          if (compressionInfo.wasCompressed) {
            successMessage += ` (compressed from ${formatFileSize(compressionInfo.originalSize)} to ${formatFileSize(compressionInfo.compressedSize)})`;
          } else if (compressionInfo.wasChunked) {
            successMessage += ` (file chunked: ${compressionInfo.totalChunks} parts, uploaded part 1 of ${compressionInfo.totalChunks})`;
          }
        }
        
        dispatch(
          showToast({
            message: successMessage,
            type: 'success',
          }),
        );
        return response.response;
      } else {
        console.error('Upload failed with status:', response.status, 'Error:', response.errorMessage);
        throw new Error(response.errorMessage?.message || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Document upload error:', error);
      dispatch(
        showToast({
          message: error.message || 'Failed to upload document',
          type: 'danger',
        }),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

    const imageDocuments = async (formData, options = {}) => {
    try {
      setLoading(true);
      
      // Extract file from FormData for compression check
      const file = formData.get('File') || formData.get('file');
      let processedFormData = formData;
      let compressionInfo = null;
      
      // If file exists and compression is enabled (default: true)
      if (file && options.enableCompression !== false) {
        try {
          console.log(`Processing image: ${file.name} (${formatFileSize(file.size)})`);
          
          // Show compression loading state
          setCompressionLoading(true);
          
          // Extract additional data from original FormData
          const additionalData = {};
          for (let [key, value] of formData.entries()) {
            if (key !== 'File' && key !== 'file') {
              additionalData[key] = value;
            }
          }
          
          // Create compressed FormData
          const result = await createFormDataWithCompression(file, additionalData);
          processedFormData = result.formData;
          compressionInfo = result.compressionInfo;
          
          if (compressionInfo.wasCompressed) {
            console.log(`Image compressed: ${formatFileSize(compressionInfo.originalSize)} → ${formatFileSize(compressionInfo.compressedSize)} (${compressionInfo.compressionRatio}% reduction)`);
          }
          
          // Hide compression loading state
          setCompressionLoading(false);
        } catch (compressionError) {
          console.error('Image compression failed:', compressionError);
          
          // Hide compression loading state
          setCompressionLoading(false);
          
          // Check if it's a size limit error
          if (compressionError.message.includes('Unable to compress file below 10MB limit')) {
            dispatch(
              showToast({
                message: `Image too large: ${compressionError.message}. Please try with a smaller image.`,
                type: 'danger',
              }),
            );
            return null;
          } else {
            // For other compression errors, try with original file
            dispatch(
              showToast({
                message: `Image compression failed: ${compressionError.message}. Trying original file...`,
                type: 'warning',
              }),
            );
          }
        }
      }
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      
      const response = await CustomerService.uploadDocument(processedFormData, config);

      if (response.status === 200) {
        if (compressionInfo) {
          if (compressionInfo.wasCompressed) {
            console.log(`Image uploaded successfully (compressed from ${formatFileSize(compressionInfo.originalSize)} to ${formatFileSize(compressionInfo.compressedSize)})`);
          } else if (compressionInfo.wasChunked) {
            console.log(`Image uploaded successfully (chunked: ${compressionInfo.totalChunks} parts, uploaded part 1 of ${compressionInfo.totalChunks})`);
          }
        }
        return response.response;
      } else {
        console.error('Upload failed with status:', response.status, 'Error:', response.errorMessage);
        throw new Error(response.errorMessage?.message || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Document upload error:', error);
      dispatch(
        showToast({
          message: error.message || 'Failed to upload document',
          type: 'danger',
        }),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };


  return { uploadDocuments, imageDocuments, loading, compressionLoading };
};
